import type { StatusCodes } from "http-status-codes";
import type { Middleware } from "openapi-fetch";

import type { RequestTimeout } from "./http.type";

interface AuthMiddlewareOptions {
  /** Read at request time, not at client construction, so a fresh sign-in is picked up immediately. */
  getToken: () => string | null | undefined;
  scheme?: string;
}

export const authMiddleware = ({ getToken, scheme = "Bearer" }: AuthMiddlewareOptions): Middleware => ({
  onRequest({ request }) {
    const token = getToken();
    if (!token) return undefined;

    request.headers.set("Authorization", `${scheme} ${token}`);
    return request;
  },
});

type TimedRequest = Request & { timeout?: RequestTimeout };

/**
 * Applies `defaultMs` unless the call passes `timeout` (ms) or `timeout: false`.
 * openapi-fetch copies unknown init keys onto the Request before middleware runs, so the override
 * is visible here without a second middleware. The caller's `signal` is always kept.
 */
export const timeoutMiddleware = (defaultMs: number): Middleware => ({
  onRequest({ request }) {
    const timeout = (request as TimedRequest).timeout;
    if (timeout === false) return undefined;

    return new Request(request, {
      signal: AbortSignal.any([request.signal, AbortSignal.timeout(timeout ?? defaultMs)]),
    });
  },
});

type StatusHandlers = Partial<Record<StatusCodes, (response: Response) => void>>;

/** Maps transport-level statuses to app decisions (sign-out, redirect, throw) without baking a policy in. */
export const statusMiddleware = (handlers: StatusHandlers): Middleware => ({
  onResponse({ response }) {
    handlers[response.status as StatusCodes]?.(response);
    return undefined;
  },
});
